import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Bookmark, Search as SearchIcon, SlidersHorizontal, X } from "lucide-react";
import { api, errMsg, specValue } from "@/lib/api";
import { useSpecSchema } from "@/context/SpecSchemaContext";
import { useAuth } from "@/context/AuthContext";
import { StatusChip, StockBadge } from "@/components/StatusChip";
import { Btn, Card, Empty, Loading, Select, inputCls, labelCls } from "@/components/Ui";

const SELECTS = [
  ["manufacturer", "Manufacturer", "manufacturers"],
  ["brand", "Brand", "brands"],
  ["category", "Category", "categories"],
  ["subcategory", "Subcategory", "subcategories"],
  ["thread_size", "Thread Size", "thread_size"],
  ["thread_type", "Thread Type", "thread_type"],
  ["cable_type", "Cable Type", "cable_type"],
  ["armour_type", "Armour Type", "armour_type"],
  ["material", "Material", "material"],
  ["ex_protection", "Ex Protection", "ex_protection"],
  ["gas_group", "Gas Group", "gas_group"],
  ["epl", "EPL", "epl"],
  ["ip_rating", "IP Rating", "ip_rating"],
  ["certificate_reference", "Certificate Reference", "certificate_reference"],
  ["country_of_manufacture", "Country of Manufacture", "countries"],
];

const EMPTY = SELECTS.reduce((a, [k]) => ({ ...a, [k]: "" }), { cable_dia: "", temp_min: "", temp_max: "" });

const SUMMARY = [
  ["thread_size", "Thread"],
  ["armour_type", "Armour"],
  ["cable_diameter", "Cable range"],
  ["ex_protection", "Ex protection"],
  ["gas_group", "Gas group"],
  ["ip_rating", "IP"],
  ["temperature", "Temperature"],
  ["certificate_reference", "IECEx certificate"],
];

export default function Search() {
  const schema = useSpecSchema();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [q, setQ] = useState(params.get("q") || "");
  const [filters, setFilters] = useState(EMPTY);
  // Result summaries follow the category schema, so a lighting result never shows "Armour".
  const summaryFields = schema.displayFor(filters.category || null).slice(0, 8);
  const [options, setOptions] = useState({});
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState(false);
  const [picked, setPicked] = useState([]);

  const toggle = (p) =>
    setPicked((s) => s.some((x) => x.id === p.id)
      ? s.filter((x) => x.id !== p.id)
      : [...s, { id: p.id, label: `${p.manufacturer_name} ${p.part_number}` }]);

  useEffect(() => {
    api.get("/filters").then(({ data }) => setOptions(data)).catch(() => {});
  }, []);

  const run = useCallback(async (term, f) => {
    setLoading(true);
    const query = {};
    if (term) query.q = term;
    Object.entries(f).forEach(([k, v]) => {
      if (v !== "" && v != null) query[k] = v;
    });
    try {
      const { data } = await api.get("/products", { params: query });
      setResults(data.results);
      setCount(data.count);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    run(params.get("q") || "", filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, filters]);

  const active = useMemo(() => Object.values(filters).filter((v) => v !== "").length, [filters]);
  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const save = async (p) => {
    try {
      await api.post("/saved", { type: "product", ref_id: p.id,
        label: `${p.manufacturer_name} ${p.part_number}` });
      toast.success("Saved to your account");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const panel = (
    <div className="space-y-4" data-testid="filters-panel">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#667788]">
          Technical filters
        </p>
        {active > 0 && (
          <button data-testid="clear-filters-btn" onClick={() => setFilters(EMPTY)} className="text-xs font-semibold text-[#B42318]">
            Clear all ({active})
          </button>
        )}
      </div>
      {SELECTS.map(([key, label, optKey]) => (
        <div key={key}>
          <label className={labelCls}>{label}</label>
          <Select
            data-testid={`filter-${key}`}
            value={filters[key]}
            onChange={(e) => set(key, e.target.value)}
            options={["", ...(options[optKey] || [])]}
          />
        </div>
      ))}
      <div>
        <label className={labelCls}>Cable diameter must accept (mm)</label>
        <input data-testid="filter-cable-dia" type="number" step="0.1" className={inputCls}
          value={filters.cable_dia} onChange={(e) => set("cable_dia", e.target.value)} placeholder="e.g. 12.5" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Min temp (°C)</label>
          <input data-testid="filter-temp-min" type="number" className={inputCls}
            value={filters.temp_min} onChange={(e) => set("temp_min", e.target.value)} placeholder="-40" />
        </div>
        <div>
          <label className={labelCls}>Max temp (°C)</label>
          <input data-testid="filter-temp-max" type="number" className={inputCls}
            value={filters.temp_max} onChange={(e) => set("temp_max", e.target.value)} placeholder="120" />
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-auto max-w-[1240px] px-5 py-10" data-testid="search-page">
      <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#147D78]">
        Product, part number &amp; equivalents
      </p>
      <h1 className="mt-3 font-display text-3xl font-black tracking-tight text-[#102A43] sm:text-4xl">
        Search products &amp; equivalents
      </h1>
      <p className="mt-3 max-w-2xl text-sm text-[#667788] md:text-base">
        Search by manufacturer, brand, product family or part number, narrow the set on real
        technical parameters, and see the technically comparable alternatives against every result.
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setParams(q.trim() ? { q: q.trim() } : {});
        }}
        className="mt-6 flex flex-col gap-2 sm:flex-row"
      >
        <div className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#667788]" />
          <input
            data-testid="search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Manufacturer, brand or part number"
            className="w-full rounded-[9px] border border-[#DFE6ED] bg-white py-3 pl-10 pr-3 text-sm outline-none focus:ring-2 focus:ring-[#147D78]"
          />
        </div>
        <Btn data-testid="search-submit-btn" type="submit" className="px-6 py-3">Search</Btn>
        <Btn type="button" variant="ghost" data-testid="toggle-filters-btn" onClick={() => setDrawer(true)} className="lg:hidden">
          <SlidersHorizontal className="h-4 w-4" /> Filters{active ? ` (${active})` : ""}
        </Btn>
      </form>

      <div className="mt-8 grid gap-8 lg:grid-cols-[280px_1fr]">
        <aside className="hidden lg:block">
          <Card>{panel}</Card>
        </aside>

        {drawer && (
          <div className="fixed inset-0 z-50 flex lg:hidden" data-testid="mobile-filters-drawer">
            <div className="flex-1 bg-black/40" onClick={() => setDrawer(false)} />
            <div className="w-[85%] max-w-sm overflow-y-auto bg-white p-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="font-display text-base font-bold text-[#102A43]">Filters</span>
                <button data-testid="close-filters-btn" onClick={() => setDrawer(false)}>
                  <X className="h-5 w-5" />
                </button>
              </div>
              {panel}
              <Btn data-testid="apply-filters-btn" onClick={() => setDrawer(false)} className="mt-6 w-full">
                Show {count} results
              </Btn>
            </div>
          </div>
        )}

        <section>
          <div className="flex items-center justify-between border-b border-[#DFE6ED] pb-3">
            <p data-testid="results-count" className="text-sm font-semibold text-[#334e68]">
              {loading ? "Searching…" : `${count} matching part${count === 1 ? "" : "s"}`}
            </p>
            <p className="hidden text-xs text-[#667788] sm:block">Matched on structured technical parameters</p>
          </div>

          {loading ? (
            <Loading label="Loading catalogue…" />
          ) : results.length === 0 ? (
            <Empty
              testId="no-results"
              title="No parts match yet"
              sub="Relax a filter, or submit an RFQ and our engineering team will source against your specification."
              action={<Link to="/rfq"><Btn variant="gold" data-testid="no-results-rfq-btn">Submit an RFQ</Btn></Link>}
            />
          ) : (
            <ul className="mt-5 space-y-3">
              {results.map((p, i) => (
                <li key={p.id} data-testid={`result-card-${p.id}`}
                  className="reveal rounded-[14px] border border-[#DFE6ED] bg-white p-5 transition-colors hover:border-[#147D78]"
                  style={{ animationDelay: `${Math.min(i, 8) * 40}ms` }}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex gap-3">
                      <input type="checkbox" data-testid={`select-product-${p.id}`}
                        checked={picked.some((x) => x.id === p.id)} onChange={() => toggle(p)}
                        className="mt-1.5 h-4 w-4 shrink-0 accent-[#147D78]" />
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#147D78]">
                        {p.manufacturer_name}
                        {p.subcategory ? ` · ${p.subcategory}` : ""}
                      </p>
                      <h3 className="mt-1 font-display text-lg font-black tracking-tight text-[#102A43]">
                        {p.part_number}
                      </h3>
                      <p className="text-sm text-[#667788]">{p.name}</p>
                      {p.stock_status && (
                        <div className="mt-2">
                          <StockBadge status={p.stock_status} location={p.stock_location}
                            testId={`result-stock-${p.id}`} />
                        </div>
                      )}
                    </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Link to={`/products/${p.id}`}>
                        <Btn data-testid={`view-product-btn-${p.id}`} className="px-4 py-2.5 text-xs">
                          Spec &amp; equivalents
                        </Btn>
                      </Link>
                      <Link to={`/rfq?product=${p.id}`}>
                        <Btn variant="ghost" data-testid={`quote-btn-${p.id}`} className="px-4 py-2.5 text-xs">
                          Request quote
                        </Btn>
                      </Link>
                      {user && user.role !== "admin" && (
                        <Btn variant="ghost" data-testid={`save-product-btn-${p.id}`} onClick={() => save(p)} className="px-3 py-2.5 text-xs">
                          <Bookmark className="h-3.5 w-3.5" />
                        </Btn>
                      )}
                    </div>
                  </div>
                  <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-[#DFE6ED] pt-4 sm:grid-cols-4">
                    {summaryFields.map(([k, label, field]) => (
                      <div key={k}>
                        <dt className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">{label}</dt>
                        <dd className="text-sm font-medium text-[#18232F]">{schema.specValue(p.spec, field)}</dd>
                      </div>
                    ))}
                  </dl>
                  {p.technical_notes && (
                    <p className="mt-3 rounded-[9px] border-l-[3px] border-[#147D78] bg-[#EAF4F3] px-3 py-2 text-xs leading-relaxed text-[#334e68]"
                      data-testid={`result-notes-${p.id}`}>
                      <span className="font-semibold text-[#147D78]">Supplier note: </span>
                      {p.technical_notes}
                    </p>
                  )}
                  {p.equivalent_count > 0 ? (
                    <div className="mt-4 rounded-[10px] border border-[#DFE6ED] bg-[#F8FAFC] p-3.5"
                      data-testid={`equivalents-${p.id}`}>
                      <p className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#667788]">
                        Technically comparable · {p.equivalent_count} alternative
                        {p.equivalent_count === 1 ? "" : "s"}
                      </p>
                      <ul className="mt-2 flex flex-wrap gap-2">
                        {p.equivalents.map((e) => (
                          <li key={e.id} className="flex items-center gap-1.5">
                            <input type="checkbox" data-testid={`select-equivalent-${p.id}-${e.id}`}
                              checked={picked.some((x) => x.id === e.id)}
                              onChange={() => toggle({ ...e, manufacturer_name: e.manufacturer_name })}
                              className="h-3.5 w-3.5 accent-[#147D78]" />
                            <Link to={`/products/${e.id}`} data-testid={`equivalent-link-${e.id}`}
                              className="flex items-center gap-2 rounded-full border border-[#DFE6ED] bg-white px-3 py-1.5 text-xs font-semibold text-[#102A43] transition-colors hover:border-[#147D78]">
                              <StatusChip status={e.verdict} compact />
                              {e.manufacturer_name} {e.part_number}
                            </Link>
                          </li>
                        ))}
                        {p.equivalent_count > p.equivalents.length && (
                          <li>
                            <Link to={`/products/${p.id}`}
                              data-testid={`more-equivalents-${p.id}`}
                              className="inline-block rounded-full px-3 py-1.5 text-xs font-bold text-[#147D78] underline">
                              +{p.equivalent_count - p.equivalents.length} more · compare all
                            </Link>
                          </li>
                        )}
                      </ul>
                    </div>
                  ) : (
                    <p className="mt-4 text-xs text-[#8598ac]" data-testid={`no-equivalents-${p.id}`}>
                      No mapped equivalents yet — open the part to compare against the wider catalogue.
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {picked.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#DFE6ED] bg-white/95 px-5 py-3 backdrop-blur"
          data-testid="compare-quote-bar">
          <div className="mx-auto flex max-w-[1240px] flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs font-bold text-[#102A43]" data-testid="picked-count">
                {picked.length} product{picked.length === 1 ? "" : "s"} selected for one RFQ
              </p>
              <p className="truncate text-[11px] text-[#667788]">
                {picked.map((x) => x.label).join(" · ")}
              </p>
            </div>
            <div className="flex gap-2">
              <Btn variant="ghost" data-testid="clear-picked-btn" onClick={() => setPicked([])}
                className="px-4 py-2.5 text-xs">Clear</Btn>
              <Link to={`/rfq?products=${picked.map((x) => x.id).join(",")}`}>
                <Btn variant="gold" data-testid="quote-selected-btn" className="px-5 py-2.5 text-xs">
                  Request quote for selected
                </Btn>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
